import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import MuiAvatar from '@mui/material/Avatar';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch, useSelector } from 'react-redux';

import { FALLBACK_CURRENCY_CODE } from '../../../constants/shell';
import { COLUMN_IDS, VISIBLE_USERS_COLS_INITIAL_STATE } from '../../../constants/users';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import useColumnsVisibility from '../../../hooks/useColumnsVisibility';
import usePopover from '../../../hooks/usePopover';
import { changeUserColumnsVisibility } from '../../../store/actions/users';
import { selectIsUserBudgetEnabled } from '../../../store/selectors/organizations';
import { selectUsersVisibleColumns } from '../../../store/selectors/users';
import { IUser } from '../../../types/users';
import { getFullName } from '../../../utils/helpers';
import { defaultTableStyles, getDefaultStickyStyles } from '../../../utils/tables';
import { getUserBudgetStatusClassname, getUserRoleLabel } from '../../../utils/users';
import { Price } from '../../index';
import { CopyableButton, PageContent, TableFilteredEmptyPlaceholder, TableMUI } from '../../_mui';

import ColumnsVisibilityPopover from '../components/ColumnsVisibilityPopover';
import { UsersContextMenu } from '../components/UsersContextMenu';
import { useUsersContextMenu } from '../hooks/useUsersContextMenu';

import styles from './UsersListView.module.scss';

const BUDGET_COLUMN_IDS = [COLUMN_IDS.BUDGET_LEFT, COLUMN_IDS.BUDGET_SPENT, COLUMN_IDS.BUDGET_TOTAL];

interface IProps {
  users: IUser[];
  searchQuery: string;
  handleRowClick: (user: IUser) => void;
  getDepartmentLabel: (id: string) => string | undefined;
  renderIntegrations: (user: Partial<IUser>) => React.ReactNode;
  onDelete: (id: string) => void;
  checkCanEditUser: (user: IUser) => boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onClearFilters?: () => void;
}

const UsersListView = ({
  users,
  handleRowClick,
  searchQuery,
  getDepartmentLabel,
  renderIntegrations,
  onDelete,
  checkCanEditUser,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onClearFilters,
}: IProps) => {
  const initialVisibleColumns = useSelector(selectUsersVisibleColumns);
  const isUserBudgetEnabled = useSelector(selectIsUserBudgetEnabled);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const {
    visibleColumns,
    columnSearchQuery,
    setColumnSearchQuery,
    handleColumnVisibilityChange,
    handleToggleAllColumns,
    handleResetColumns,
    areAllColumnsVisible,
  } = useColumnsVisibility({
    initialState: initialVisibleColumns,
    requiredColumnIds: [COLUMN_IDS.USER, COLUMN_IDS.ACTIONS],
    onChange: (newState: Record<string, boolean>) => dispatch(changeUserColumnsVisibility(newState)),
    defaultColumns: VISIBLE_USERS_COLS_INITIAL_STATE,
  });

  const {
    menuAnchorEl,
    handleMenuOpen,
    handleMenuClose,
    handleEdit,
    handleDelete,
    handleSkipCrm,
    isSkipCrmAllowedInOrg,
    selectedItem,
  } = useUsersContextMenu<IUser>({
    onEdit: handleRowClick,
    onDelete,
    canEdit: checkCanEditUser,
  });

  const {
    anchorEl: visibleColsAnchorEl,
    position: popoverPosition,
    isOpen: isVisibleColsPopoverOpen,
    handleOpen: handleVisibleColsMenuOpen,
    handleClose: handleVisibleColsMenuClose,
  } = usePopover();

  const { getRequiredPriceAndCurrency, displayCurrency } = useCurrency();

  const defaultColumnEvents = useMemo(
    () => ({
      onBodyCellDoubleClick: handleRowClick,
      onCellContextMenu: (e: React.MouseEvent<HTMLElement, MouseEvent>, user: IUser, columnId?: string) => {
        if (columnId !== COLUMN_IDS.ACTIONS) {
          e.preventDefault();
          setMenuPosition({
            top: e.pageY,
            left: e.pageX,
          });
          handleMenuOpen(e, user);
        }
      },
    }),
    [handleMenuOpen, handleRowClick],
  );

  const handleLocalMenuClose = useCallback(() => {
    setMenuPosition(null);
    handleMenuClose();
  }, [handleMenuClose]);

  const allColumns = useMemo(() => {
    const { stickyLeftCellStyle, actionsCellStyle } = getDefaultStickyStyles(theme);
    const { bodyCellStyle } = defaultTableStyles(theme);

    return [
      {
        id: COLUMN_IDS.USER,
        label: 'User',
        ...defaultColumnEvents,
        headerCellStyle: {
          ...stickyLeftCellStyle,
          minWidth: 200,
          zIndex: { xs: '2 !important', sm: '11 !important' },
          paddingLeft: 2.5,
        },
        bodyCellStyle: {
          ...stickyLeftCellStyle,
          cursor: 'pointer',
          minWidth: 200,
        },
        render: (user: IUser) => {
          const { first_name, last_name, picture, email } = user;
          const fullName = getFullName(first_name, last_name);
          const firstLetter = (fullName || email)?.trim()?.[0]?.toUpperCase();

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MuiAvatar
                src={picture}
                alt={fullName || email}
                slotProps={{
                  img: {
                    referrerPolicy: 'no-referrer-when-downgrade',
                  },
                }}
              >
                {firstLetter}
              </MuiAvatar>
              <Box sx={{ minWidth: 0 }}>
                <CopyableButton
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    lineHeight: '1.2em',
                    maxHeight: '2.4em',
                    textAlign: 'start',
                    color: theme.sass.ebony,
                    fontWeight: theme.typography.fontWeightMedium,
                  }}
                  value={fullName}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                  toastLabel="Fullname"
                >
                  <Highlighter
                    highlightClassName={styles.mark}
                    searchWords={[searchQuery]}
                    autoEscape
                    textToHighlight={fullName}
                  />
                </CopyableButton>
                <CopyableButton
                  style={{
                    width: '100%',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    textAlign: 'start',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: theme.sass.ebony,
                  }}
                  value={email}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                  toastLabel="Email"
                >
                  <Highlighter
                    highlightClassName={styles.mark}
                    className={styles.text}
                    searchWords={[searchQuery]}
                    autoEscape
                    textToHighlight={email}
                  />
                </CopyableButton>
              </Box>
            </Box>
          );
        },
      },
      {
        id: COLUMN_IDS.INTEGRATIONS,
        label: 'Integrations',
        ...defaultColumnEvents,
        bodyCellStyle,
        render: (user: IUser) => renderIntegrations({ integration: user.integration, uid: user.uid }),
      },
      {
        id: COLUMN_IDS.ROLE,
        label: 'Role',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => <Typography>{getUserRoleLabel(user.admin_type)}</Typography>,
      },
      {
        id: COLUMN_IDS.DEPARTMENT,
        label: 'Department',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => <Typography>{getDepartmentLabel(user.department_id)}</Typography>,
      },
      {
        id: COLUMN_IDS.BUDGET_LEFT,
        label: 'Budget left',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => {
          const remainingBudget =
            user.available_budget - user.used_budget < 0 ? 0 : Math.floor(user.available_budget - user.used_budget);
          return (
            <Price
              value={
                displayCurrency
                  ? getRequiredPriceAndCurrency({ price: remainingBudget, currency_code: user.budget_currency })
                  : { price: remainingBudget, currency: user.budget_currency || FALLBACK_CURRENCY_CODE }
              }
              thousandSeparator
              fixedDecimalScale={false}
              className={cn(
                styles.budgetLeft,
                styles[getUserBudgetStatusClassname(user.available_budget, user.used_budget)],
              )}
            />
          );
        },
      },
      {
        id: COLUMN_IDS.BUDGET_SPENT,
        label: 'Budget spent',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => (
          <Price
            value={
              displayCurrency
                ? getRequiredPriceAndCurrency({
                    price: Math.floor(user.used_budget),
                    currency_code: user.budget_currency,
                  })
                : { price: user.used_budget, currency: user.budget_currency || FALLBACK_CURRENCY_CODE }
            }
            thousandSeparator
            fixedDecimalScale={false}
          />
        ),
      },
      {
        id: COLUMN_IDS.BUDGET_TOTAL,
        label: 'Budget total',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => (
          <Price
            value={
              displayCurrency
                ? getRequiredPriceAndCurrency({
                    price: Math.floor(user.available_budget),
                    currency_code: user.budget_currency,
                  })
                : { price: user.available_budget, currency: user.budget_currency || FALLBACK_CURRENCY_CODE }
            }
            thousandSeparator
            fixedDecimalScale={false}
            className={styles.budgetAvailable}
          />
        ),
      },
      {
        id: COLUMN_IDS.LAST_LOGIN,
        label: 'Last login',
        bodyCellStyle,
        ...defaultColumnEvents,
        render: (user: IUser) => {
          const lastLoginDate = user.last_sign_in_time ? format(parseISO(user.last_sign_in_time), 'dd/MM/yyyy') : '-';
          return <Typography>{lastLoginDate}</Typography>;
        },
      },
      {
        id: COLUMN_IDS.ACTIONS,
        label: (
          <Tooltip title="Show/hide columns" arrow placement="top">
            <TuneIcon color="action" />
          </Tooltip>
        ),
        headerCellStyle: actionsCellStyle,
        bodyCellStyle: actionsCellStyle,
        onHeaderCellClick: handleVisibleColsMenuOpen,
        render: (user: IUser) => (
          <Box
            role="button"
            sx={{
              cursor: 'pointer',
            }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              setMenuPosition({
                top: rect.bottom,
                left: rect.left - 140,
              });
              handleMenuOpen(e, user);
            }}
          >
            <MoreHorizIcon color="action" />
          </Box>
        ),
      },
    ];
  }, [
    theme,
    defaultColumnEvents,
    handleVisibleColsMenuOpen,
    searchQuery,
    renderIntegrations,
    getDepartmentLabel,
    displayCurrency,
    getRequiredPriceAndCurrency,
    handleMenuOpen,
  ]);

  const availableColumns = useMemo(
    () =>
      allColumns.filter((col) => {
        const isBudgetColumn = BUDGET_COLUMN_IDS.includes(col.id);
        const shouldHideBudgetColumn = isBudgetColumn && !isUserBudgetEnabled;
        return !shouldHideBudgetColumn;
      }),
    [allColumns, isUserBudgetEnabled],
  );

  const columns = useMemo(() => {
    return availableColumns.filter((column) => visibleColumns[column.id]);
  }, [availableColumns, visibleColumns]);

  const placeholder = useMemo(() => {
    return <TableFilteredEmptyPlaceholder itemType="users" onClearFilters={onClearFilters} />;
  }, [onClearFilters]);

  return (
    <PageContent>
      <TableMUI
        activeRow={selectedItem}
        menuAnchorEl={menuAnchorEl}
        handleMenuClose={handleLocalMenuClose}
        maxHeight="unset"
        containerStyle={{ width: '100%' }}
        data={users}
        columns={columns}
        page={page}
        rowsPerPage={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        isLoading={false}
        emptyPlaceholderComponent={placeholder}
      />
      <UsersContextMenu
        anchorEl={menuAnchorEl}
        menuPosition={menuPosition}
        selectedItem={selectedItem}
        isSkipCrmAllowedInOrg={isSkipCrmAllowedInOrg}
        checkCanEditUser={checkCanEditUser}
        onClose={handleLocalMenuClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onSkipCrm={handleSkipCrm}
      />
      <ColumnsVisibilityPopover
        anchorEl={visibleColsAnchorEl}
        position={popoverPosition}
        open={isVisibleColsPopoverOpen}
        onClose={handleVisibleColsMenuClose}
        columns={availableColumns}
        visibleColumns={visibleColumns}
        searchQuery={columnSearchQuery}
        onSearchChange={setColumnSearchQuery}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        onToggleAll={handleToggleAllColumns}
        onReset={handleResetColumns}
        requiredColumnIds={[COLUMN_IDS.USER, COLUMN_IDS.ACTIONS]}
        areAllColumnsVisible={areAllColumnsVisible}
      />
    </PageContent>
  );
};

export default UsersListView;
